import React, { useEffect, useState } from 'react';
import { mcpTokensAPI } from '../../lib/api';

// "Connect Claude" — issue and revoke your MCP tokens. A token is yours, not
// this workspace's: it works in every workspace you belong to, with your role
// in each, so the list here is your tokens from all of them.
//
// The secret is shown exactly once, at creation: the server stores only a
// sha256 hash. Scope selection is deliberately explicit rather than an
// all-or-nothing toggle, because the token's scopes are what the MCP server
// filters its tool list by — a read-only token literally cannot see the
// on-air tools, which is what makes it safe to leave connected during a show.
export default function ConnectClaudeSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [issued, setIssued] = useState(null);
  const [copied, setCopied] = useState('');

  const [name, setName] = useState('');
  const [scopes, setScopes] = useState([]);
  const [expiryDays, setExpiryDays] = useState('');

  useEffect(() => {
    mcpTokensAPI.list()
      .then(r => { setData(r.data); setScopes(r.data.default_scopes); })
      .catch(() => setError('Failed to load MCP tokens'))
      .finally(() => setLoading(false));
  }, []);

  function toggleScope(scope) {
    setScopes(prev => (prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]));
  }

  async function copy(text, key) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 1500);
    } catch { /* clipboard unavailable — the field is select-all anyway */ }
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!name.trim()) { setError('Give the token a name so you can tell it apart later'); return; }
    if (scopes.length === 0) { setError('Pick at least one scope'); return; }

    setCreating(true);
    setError('');
    try {
      const r = await mcpTokensAPI.create({
        name: name.trim(),
        scopes,
        expires_in_days: expiryDays ? parseInt(expiryDays, 10) : null,
      });
      setIssued(r.data);
      setData(d => ({ ...d, tokens: [r.data, ...d.tokens] }));
      setName('');
      setExpiryDays('');
      setShowForm(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create token');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(token) {
    if (!confirm(`Revoke "${token.name}"? Any Claude session using it stops working on its next request.`)) return;
    try {
      await mcpTokensAPI.revoke(token.id);
      setData(d => ({ ...d, tokens: d.tokens.filter(t => t.id !== token.id) }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke token');
    }
  }

  const inputCls = 'bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500';

  if (loading) {
    return (
      <div>
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Connect Claude</div>
        <div className="text-gray-500 text-sm py-2">Loading…</div>
      </div>
    );
  }

  return (
    <div>
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Connect Claude</div>
      <p className="text-xs text-gray-600 mb-3 leading-relaxed">
        Give Claude access to Mission Control over MCP — rundowns, imports, automation tools and admin.
        A token is yours, not this workspace&rsquo;s: it works in every workspace you belong to, with your
        role in each, and <span className="font-mono">live:execute</span> and{' '}
        <span className="font-mono">workspace:admin</span> only apply where you are an admin.
        Claude only ever sees the tools the token&rsquo;s scopes allow.
      </p>

      {issued && (
        <div className="mb-3 p-3 bg-blue-950/40 border border-blue-800 rounded space-y-2">
          <div className="text-xs font-semibold text-blue-300">
            Copy this now — it is never shown again
          </div>
          <div className="flex gap-2">
            <code className="flex-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-xs text-blue-400 font-mono select-all break-all">
              {issued.token}
            </code>
            <button
              onClick={() => copy(issued.token, 'token')}
              className="px-3 py-2 bg-[#1a1a1a] hover:bg-[#222] border border-[#2a2a2a] rounded text-xs text-gray-300 transition-colors whitespace-nowrap self-start"
            >
              {copied === 'token' ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="text-xs text-gray-400 pt-1 font-medium">
            To connect: copy the command below, paste it into a terminal, then start a
            <span className="text-white"> new </span>
            Claude Code session and run <code className="text-blue-400">/mcp</code> to check it says Connected.
          </div>
          <div className="flex gap-2">
            <code className="flex-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-[11px] text-gray-400 font-mono select-all break-all">
              {issued.claude_code_command}
            </code>
            <button
              onClick={() => copy(issued.claude_code_command, 'cmd')}
              className="px-3 py-2 bg-[#1a1a1a] hover:bg-[#222] border border-[#2a2a2a] rounded text-xs text-gray-300 transition-colors whitespace-nowrap self-start"
            >
              {copied === 'cmd' ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button onClick={() => setIssued(null)} className="text-xs text-gray-500 hover:text-white transition-colors">
            Done
          </button>
        </div>
      )}

      {data?.tokens?.length > 0 && (
        <div className="space-y-1 mb-3">
          {data.tokens.map(token => (
            <div key={token.id} className="flex items-center gap-3 px-3 py-2 bg-[#0a0a0a] border border-[#1a1a1a] rounded">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate flex items-center gap-2">
                  {token.name}
                  {token.status !== 'active' && (
                    <span className="text-[10px] uppercase tracking-wide text-red-400 border border-red-900 rounded px-1">
                      {token.status}
                    </span>
                  )}
                  {token.scopes?.includes('live:execute') && token.status === 'active' && (
                    <span className="text-[10px] uppercase tracking-wide text-amber-400 border border-amber-900 rounded px-1">
                      live
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 font-mono truncate">
                  {token.scopes?.join(' · ')}
                </div>
                <div className="text-[11px] text-gray-600">
                  {token.last_used_at
                    ? `Last used ${new Date(token.last_used_at).toLocaleString()}`
                    : 'Never used'}
                  {token.expires_at ? ` · expires ${new Date(token.expires_at).toLocaleDateString()}` : ''}
                </div>
              </div>
              {token.status === 'active' && (
                <button
                  onClick={() => handleRevoke(token)}
                  className="text-xs text-gray-400 hover:text-red-400 transition-colors"
                >
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-xs text-red-400 mb-2">{error}</p>}

      {!showForm ? (
        <button
          onClick={() => { setShowForm(true); setError(''); }}
          className="px-3 py-2 bg-blue-700 hover:bg-blue-600 text-white text-xs font-medium rounded transition-colors"
        >
          Create token
        </button>
      ) : (
        <form onSubmit={handleCreate} className="space-y-2">
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Name (e.g. Simon's laptop — Claude Code)"
            className={`w-full ${inputCls}`}
          />

          <div className="space-y-1 pt-1">
            {data?.available_scopes?.map(s => (
              <label
                key={s.scope}
                className={`flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-[#0a0a0a] ${
                  s.superadmin_only ? 'opacity-60' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={scopes.includes(s.scope)}
                  onChange={() => toggleScope(s.scope)}
                  className="mt-0.5 accent-blue-600"
                />
                <span className="min-w-0">
                  <span className="text-xs font-mono text-white">{s.scope}</span>
                  {(s.admin_only || s.superadmin_only) && (
                    <span className="ml-1 text-[10px] uppercase tracking-wide text-amber-500">
                      {s.superadmin_only ? 'superadmin' : 'admin only'}
                    </span>
                  )}
                  <span className="block text-[11px] text-gray-500 leading-snug">{s.description}</span>
                </span>
              </label>
            ))}
          </div>

          <div className="flex gap-2 items-center">
            <input
              type="number"
              value={expiryDays}
              onChange={e => setExpiryDays(e.target.value)}
              placeholder="Expires in (days)"
              className={`w-40 ${inputCls}`}
            />
            <span className="text-[11px] text-gray-600">Leave blank for no expiry</span>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={creating}
              className="px-3 py-2 bg-blue-700 hover:bg-blue-600 disabled:opacity-40 text-white text-xs font-medium rounded transition-colors"
            >
              {creating ? 'Creating…' : 'Create token'}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setError(''); }}
              className="px-3 py-2 text-xs text-gray-500 hover:text-white transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {data?.mcp_url && (
        <p className="text-[11px] text-gray-600 mt-2 font-mono break-all">
          Endpoint: {data.mcp_url}
        </p>
      )}
    </div>
  );
}
